/**
 * FAQ Tab Component
 *
 * Admin CMS for managing FAQ content with multi-tenant and localization support.
 * Features:
 * - Universal FAQs management (superadmin only)
 * - Tenant-specific FAQs
 * - Override/exclude universal FAQs per tenant
 * - Multi-language content editing
 * - Markdown preview
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import {
  faqApi,
  type AdminFAQ,
  type AdminCategory,
  type TenantOverride,
  type CreateFAQRequest,
  type UpdateFAQRequest,
  type CreateCategoryRequest,
  type LocalizedContent,
} from '@/services/faqApi';
import { useAuth } from '@/contexts/AuthContext';

// Supported languages
const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'it', name: 'Italiano' },
];

// Icon mapping for FAQ categories
const CategoryIcon: React.FC<{ name: string; className?: string }> = ({ name, className = "w-5 h-5" }) => {
  const icons: Record<string, React.ReactNode> = {
    'rocket': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    'microphone': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    ),
    'credit-card': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    'shield': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    'question': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    'book': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    'cog': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  };

  return <span className="inline-flex text-gray-600">{icons[name] || icons['question']}</span>;
};

type ViewMode = 'universal' | 'tenant' | 'overrides';

interface FAQFormData {
  slug: string;
  category_id: string;
  sort_order: number;
  is_published: boolean;
  localized_content: Record<string, LocalizedContent>;
}

interface CategoryFormData {
  slug: string;
  icon: string;
  sort_order: number;
  is_active: boolean;
  localized_names: Record<string, string>;
}

export default function FAQTab() {
  const { t } = useTranslation('admin');
  const { user } = useAuth();

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('universal');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedTenantId, _setSelectedTenantId] = useState<string>('default');

  // Data state
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [faqs, setFaqs] = useState<AdminFAQ[]>([]);
  const [overrides, setOverrides] = useState<TenantOverride[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [showFAQForm, setShowFAQForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<AdminFAQ | null>(null);
  const [editingCategory, setEditingCategory] = useState<AdminCategory | null>(null);
  const [currentLang, setCurrentLang] = useState('en');

  // Form data
  const [faqForm, setFaqForm] = useState<FAQFormData>({
    slug: '',
    category_id: '',
    sort_order: 0,
    is_published: true,
    localized_content: {
      en: { question: '', answer: '' },
      it: { question: '', answer: '' },
    },
  });

  const [categoryForm, setCategoryForm] = useState<CategoryFormData>({
    slug: '',
    icon: '',
    sort_order: 0,
    is_active: true,
    localized_names: { en: '', it: '' },
  });

  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  const isSuperAdmin = user?.is_superadmin ?? false;

  // Fetch data based on view mode
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      if (viewMode === 'universal') {
        const [cats, faqList] = await Promise.all([
          faqApi.listUniversalCategories(),
          faqApi.listUniversalFAQs(),
        ]);
        setCategories(cats);
        setFaqs(faqList);
      } else if (viewMode === 'tenant') {
        const [cats, faqList] = await Promise.all([
          faqApi.listUniversalCategories(), // Use universal categories
          faqApi.listTenantFAQs(selectedTenantId),
        ]);
        setCategories(cats);
        setFaqs(faqList);
      } else if (viewMode === 'overrides') {
        const [cats, overrideList] = await Promise.all([
          faqApi.listUniversalCategories(),
          faqApi.listTenantOverrides(selectedTenantId),
        ]);
        setCategories(cats);
        setOverrides(overrideList);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [viewMode, selectedTenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Clear success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [successMessage]);

  // FAQ CRUD operations
  const handleCreateFAQ = async () => {
    try {
      const data: CreateFAQRequest = {
        category_id: faqForm.category_id,
        slug: faqForm.slug,
        sort_order: faqForm.sort_order,
        is_published: faqForm.is_published,
        localized_content: faqForm.localized_content,
      };

      if (viewMode === 'universal') {
        await faqApi.createUniversalFAQ(data);
      } else {
        await faqApi.createTenantFAQ(selectedTenantId, data);
      }

      setSuccessMessage(t('faq.created'));
      setShowFAQForm(false);
      resetFAQForm();
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create FAQ');
    }
  };

  const handleUpdateFAQ = async () => {
    if (!editingFAQ) return;

    try {
      const data: UpdateFAQRequest = {
        slug: faqForm.slug,
        category_id: faqForm.category_id,
        sort_order: faqForm.sort_order,
        is_published: faqForm.is_published,
        localized_content: faqForm.localized_content,
      };

      if (viewMode === 'universal') {
        await faqApi.updateUniversalFAQ(editingFAQ.id, data);
      } else {
        await faqApi.updateTenantFAQ(selectedTenantId, editingFAQ.id, data);
      }

      setSuccessMessage(t('faq.updated'));
      setShowFAQForm(false);
      setEditingFAQ(null);
      resetFAQForm();
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update FAQ');
    }
  };

  const handleDeleteFAQ = async (faq: AdminFAQ) => {
    if (!confirm(t('faq.confirmDelete'))) return;

    try {
      if (viewMode === 'universal') {
        await faqApi.deleteUniversalFAQ(faq.id);
      } else {
        await faqApi.deleteTenantFAQ(selectedTenantId, faq.id);
      }

      setSuccessMessage(t('faq.deleted'));
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete FAQ');
    }
  };

  // Category CRUD operations
  const handleCreateCategory = async () => {
    try {
      const data: CreateCategoryRequest = {
        slug: categoryForm.slug,
        localized_names: categoryForm.localized_names,
        icon: categoryForm.icon || undefined,
        sort_order: categoryForm.sort_order,
        is_active: categoryForm.is_active,
      };

      await faqApi.createUniversalCategory(data);
      setSuccessMessage(t('faq.categoryCreated'));
      setShowCategoryForm(false);
      resetCategoryForm();
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      await faqApi.updateUniversalCategory(editingCategory.id, {
        slug: categoryForm.slug,
        localized_names: categoryForm.localized_names,
        icon: categoryForm.icon || undefined,
        sort_order: categoryForm.sort_order,
        is_active: categoryForm.is_active,
      });

      setSuccessMessage(t('faq.categoryUpdated'));
      setShowCategoryForm(false);
      setEditingCategory(null);
      resetCategoryForm();
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (category: AdminCategory) => {
    if (!confirm(t('faq.confirmDeleteCategory'))) return;

    try {
      await faqApi.deleteUniversalCategory(category.id);
      setSuccessMessage(t('faq.categoryDeleted'));
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  // Override operations
  const handleSetOverride = async (faqSlug: string, action: 'override' | 'exclude') => {
    try {
      await faqApi.setTenantOverride(selectedTenantId, faqSlug, { action });
      setSuccessMessage(action === 'exclude' ? t('faq.excluded') : t('faq.overrideSet'));
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set override');
    }
  };

  // Export to prevent unused variable warning
  void handleSetOverride;

  const handleRemoveOverride = async (faqSlug: string) => {
    try {
      await faqApi.removeTenantOverride(selectedTenantId, faqSlug);
      setSuccessMessage(t('faq.overrideRemoved'));
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove override');
    }
  };

  // Form helpers
  const resetFAQForm = () => {
    setFaqForm({
      slug: '',
      category_id: categories[0]?.id || '',
      sort_order: 0,
      is_published: true,
      localized_content: {
        en: { question: '', answer: '' },
        it: { question: '', answer: '' },
      },
    });
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      slug: '',
      icon: '',
      sort_order: 0,
      is_active: true,
      localized_names: { en: '', it: '' },
    });
  };

  const openEditFAQ = (faq: AdminFAQ) => {
    setEditingFAQ(faq);
    setFaqForm({
      slug: faq.slug,
      category_id: faq.category_id,
      sort_order: faq.sort_order,
      is_published: faq.is_published,
      localized_content: faq.localized_content as Record<string, LocalizedContent>,
    });
    setShowFAQForm(true);
  };

  const openEditCategory = (category: AdminCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      slug: category.slug,
      icon: category.icon || '',
      sort_order: category.sort_order,
      is_active: category.is_active,
      localized_names: category.localized_names,
    });
    setShowCategoryForm(true);
  };

  const getCategoryName = (categoryId: string): string => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.localized_names.en || categoryId;
  };

  // Render
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">{t('faq.title')}</h2>
        <div className="flex items-center gap-4">
          {/* View mode tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {isSuperAdmin && (
              <button
                onClick={() => setViewMode('universal')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'universal'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('faq.universal')}
              </button>
            )}
            <button
              onClick={() => setViewMode('tenant')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'tenant'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('faq.tenant')}
            </button>
            <button
              onClick={() => setViewMode('overrides')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'overrides'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('faq.overrides')}
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Universal/Tenant FAQs View */}
          {(viewMode === 'universal' || viewMode === 'tenant') && (
            <div className="space-y-6">
              {/* Categories Section */}
              {viewMode === 'universal' && isSuperAdmin && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('faq.categories')}
                    </h3>
                    <button
                      onClick={() => {
                        resetCategoryForm();
                        setEditingCategory(null);
                        setShowCategoryForm(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      + {t('faq.addCategory')}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className="border rounded-lg p-4 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CategoryIcon name={category.icon || 'question'} className="w-5 h-5 mr-2" />
                            <span className="font-medium">
                              {category.localized_names.en}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {category.faq_count} FAQs
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-500">
                          /{category.slug}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={() => openEditCategory(category)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQs Section */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {viewMode === 'universal' ? t('faq.universalFaqs') : t('faq.tenantFaqs')}
                  </h3>
                  <button
                    onClick={() => {
                      resetFAQForm();
                      setEditingFAQ(null);
                      setShowFAQForm(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    + {t('faq.addFaq')}
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                          {t('faq.question')}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                          {t('faq.category')}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                          {t('faq.status')}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                          {t('faq.views')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
                          {t('common.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {faqs.map((faq) => (
                        <tr key={faq.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">
                              {(faq.localized_content as Record<string, LocalizedContent>).en?.question || faq.slug}
                            </div>
                            <div className="text-sm text-gray-500">/{faq.slug}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {getCategoryName(faq.category_id)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                faq.is_published
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {faq.is_published ? t('faq.published') : t('faq.draft')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{faq.view_count}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => openEditFAQ(faq)}
                              className="text-blue-600 hover:text-blue-800 mr-3"
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              onClick={() => handleDeleteFAQ(faq)}
                              className="text-red-600 hover:text-red-800"
                            >
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {faqs.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                            {t('faq.noFaqs')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Overrides View */}
          {viewMode === 'overrides' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('faq.tenantOverrides')}
              </h3>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        {t('faq.faqSlug')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        {t('faq.action')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        {t('faq.category')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {overrides.map((override) => (
                      <tr key={override.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {override.faq_slug}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              override.action === 'exclude'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {override.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {override.category_slug || '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleRemoveOverride(override.faq_slug)}
                            className="text-red-600 hover:text-red-800"
                          >
                            {t('faq.removeOverride')}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {overrides.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                          {t('faq.noOverrides')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* FAQ Form Modal */}
      {showFAQForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold">
                {editingFAQ ? t('faq.editFaq') : t('faq.createFaq')}
              </h3>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('faq.slug')}
                  </label>
                  <input
                    type="text"
                    value={faqForm.slug}
                    onChange={(e) => setFaqForm({ ...faqForm, slug: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="getting-started"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('faq.category')}
                  </label>
                  <select
                    value={faqForm.category_id}
                    onChange={(e) => setFaqForm({ ...faqForm, category_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('faq.selectCategory')}</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.localized_names.en}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('faq.sortOrder')}
                  </label>
                  <input
                    type="number"
                    value={faqForm.sort_order}
                    onChange={(e) =>
                      setFaqForm({ ...faqForm, sort_order: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_published"
                    checked={faqForm.is_published}
                    onChange={(e) =>
                      setFaqForm({ ...faqForm, is_published: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="is_published" className="text-sm font-medium text-gray-700">
                    {t('faq.published')}
                  </label>
                </div>
              </div>

              {/* Language tabs */}
              <div>
                <div className="flex gap-2 mb-4">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setCurrentLang(lang.code)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentLang === lang.code
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className={`ml-auto px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      showPreview
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {showPreview ? t('faq.hidePreview') : t('faq.showPreview')}
                  </button>
                </div>

                {/* Content for current language */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('faq.question')} ({currentLang.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={faqForm.localized_content[currentLang]?.question || ''}
                      onChange={(e) =>
                        setFaqForm({
                          ...faqForm,
                          localized_content: {
                            ...faqForm.localized_content,
                            [currentLang]: {
                              ...faqForm.localized_content[currentLang],
                              question: e.target.value,
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="How do I start using {{brand_name}}?"
                    />
                  </div>

                  <div className={showPreview ? 'grid grid-cols-2 gap-4' : ''}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('faq.answer')} ({currentLang.toUpperCase()}) - Markdown
                      </label>
                      <textarea
                        value={faqForm.localized_content[currentLang]?.answer || ''}
                        onChange={(e) =>
                          setFaqForm({
                            ...faqForm,
                            localized_content: {
                              ...faqForm.localized_content,
                              [currentLang]: {
                                ...faqForm.localized_content[currentLang],
                                answer: e.target.value,
                              },
                            },
                          })
                        }
                        rows={10}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                        placeholder="Use **bold**, *italic*, and markdown formatting..."
                      />
                    </div>

                    {showPreview && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('faq.preview')}
                        </label>
                        <div className="border rounded-lg p-4 prose prose-sm max-w-none h-64 overflow-y-auto bg-gray-50">
                          <ReactMarkdown>
                            {faqForm.localized_content[currentLang]?.answer || ''}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <p className="mt-2 text-sm text-gray-500">
                  {t('faq.brandPlaceholderHint')}
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowFAQForm(false);
                  setEditingFAQ(null);
                  resetFAQForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={editingFAQ ? handleUpdateFAQ : handleCreateFAQ}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingFAQ ? t('common.save') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold">
                {editingCategory ? t('faq.editCategory') : t('faq.createCategory')}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('faq.slug')}
                </label>
                <input
                  type="text"
                  value={categoryForm.slug}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, slug: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="getting-started"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('faq.icon')} (emoji)
                </label>
                <input
                  type="text"
                  value={categoryForm.icon}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, icon: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="🚀"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('faq.nameEn')}
                  </label>
                  <input
                    type="text"
                    value={categoryForm.localized_names.en || ''}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        localized_names: {
                          ...categoryForm.localized_names,
                          en: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Getting Started"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('faq.nameIt')}
                  </label>
                  <input
                    type="text"
                    value={categoryForm.localized_names.it || ''}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        localized_names: {
                          ...categoryForm.localized_names,
                          it: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Per iniziare"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('faq.sortOrder')}
                  </label>
                  <input
                    type="number"
                    value={categoryForm.sort_order}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        sort_order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={categoryForm.is_active}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, is_active: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    {t('faq.active')}
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCategoryForm(false);
                  setEditingCategory(null);
                  resetCategoryForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingCategory ? t('common.save') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
